<template>
	<VList
		ref="profileSwitcher"
		:aria-label="t('navigationMenu.profileSwitcher')"
		class="the-navigation-menu-profile-switcher px-4 mb-md-2"
		density="compact"
		nav
	>
		<VListItem
			class="pl-1"
			:title="activeDisplayProfile?.title"
			:subtitle="activeDisplayProfile?.subtitle"
			:link="userStore.hasMultipleProfiles"
			data-testid="navigationProfileSwitcher"
		>
			<template #prepend>
				<CommonAvatar
					:src="activeDisplayProfile?.avatar"
					size="40"
				/>
			</template>
			<template
				v-if="userStore.hasMultipleProfiles && isExpanded"
				#append
			>
				<VIcon
					icon="swap_horiz"
					size="16"
				/>
			</template>
			<VMenu
				v-if="userStore.hasMultipleProfiles"
				v-model="isMenuActive"
				theme="TwLight"
				activator="parent"
				location="bottom"
				:close-on-content-click="false"
			>
				<VCard
					color="background"
					width="275"
					border
				>
					<VCardTitle class="text-h4 text-high-emphasis">
						{{ t('profile', 0) }}
					</VCardTitle>
					<VCardText class="pa-3">
						<BaseField
							v-slot="{id}"
							:label="t('search')"
							hide-label
							class="mb-4"
						>
							<VTextField
								:id="id"
								ref="fieldProfilesSearch"
								v-model="profilesSearch"
								:placeholder="t('search')"
								hide-details
								data-testid="navigationProfileSwitcherSearch"
								autocomplete="off"
							/>
						</BaseField>
						<template
							v-for="(group, groupIndex) in profilesGrouped"
							:key="group.role"
						>
							<div
								:id="`navigation-menu-profile-switcher-group-name-${groupIndex}`"
								class="text-caption text-medium-emphasis px-4"
							>
								{{ group.displayName }}
							</div>
							<VList
								bg-color="background"
								nav
								:aria-labelledby="`navigation-menu-profile-switcher-group-name-${groupIndex}`"
							>
								<VListItem
									v-for="profile, index in group.profiles"
									:key="profile.uuid"
									class="px-3 text-decoration-none"
									:disabled="profile.disabled"
									:data-testid="getTestId(group.role.toString(), index)"
									:link="userStore.profile?.uuid !== profile.uuid"
									:href="profile.href ?? undefined"
									:aria-labelledby="`${profile.uuid}-title`"
									@click="onProfileClick(profile)"
								>
									<template #prepend>
										<div class="position-relative">
											<CommonAvatar
												:src="profile.avatar"
												size="36"
											/>
											<VIcon
												v-if="userStore.profile?.uuid === profile.uuid"
												class="position-absolute the-navigation-menu-profile-switcher__checkmark"
												color="success"
												icon="check_circle"
											/>
										</div>
									</template>
									<template #title>
										<span
											:id="`${profile.uuid}-title`"
											class="px-3 text-body-2 font-weight-semibold"
										>
											{{ profile.title }}
										</span>
									</template>
									<template
										#append
									>
										<VBtnIcon
											:class="{ 'pointer-events-none': profile.is_default }"
											:color="profile.is_default ? 'yellow' : 'neutral-400'"
											icon="star"
											size="small"
											:aria-label="t('navigationMenu.setAsDefault')"
											@click.stop="profile.is_default ? undefined : userStore.updateDefaultUserProfile(profile.uuid)"
										/>
									</template>
									<VTooltip
										v-if="!mobile"
										:data-testid="getTooltipTestId(group.role.toString(), index)"
										activator="parent"
										location="right"
										:aria-label="profile.title"
									>
										{{ profile.title }}
									</VTooltip>
								</VListItem>
							</VList>
						</template>
					</VCardText>
				</VCard>
			</VMenu>
			<VTooltip
				v-if="!isExpanded"
				activator="parent"
				location="right"
				open-delay="500"
				:aria-label="activeDisplayProfile?.title"
			>
				{{ activeDisplayProfile?.title }}
			</VTooltip>
		</VListItem>
	</VList>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import { useUserProfiles, type IDisplayProfile } from '@/composables/useUserProfiles';
import { BaseField, CommonAvatar } from '@teamworksdev/vuetify';
import { useUserStore } from '@/stores/userStore';
import { VMenu } from 'vuetify/components/VMenu';
import { useRouter } from 'vue-router';
import { $ResetPinia } from '@/plugins/pinia';
import { canAccessRoute } from '@/router';

defineProps<{
	isExpanded: boolean;
}>();

const { mobile } = useDisplay();
const { t } = useI18n();
const router = useRouter();

const {
	profilesGrouped,
	profilesSearch,
	activeDisplayProfile,
} = useUserProfiles();

const isMenuActive = ref(false);
const userStore = useUserStore();

function onProfileClick(profile: IDisplayProfile) {
	if (profile.href) return;
	if (userStore.profile?.uuid !== profile.uuid) switchProfile(profile.uuid);
}

async function switchProfile(profileId: string) {
	isMenuActive.value = false;
	profilesSearch.value = '';
	router.replace({ query: {} });
	await userStore.setProfile(profileId);
	await onProfileSwitch();
	$ResetPinia().profile?.();
}

async function onProfileSwitch() {
	const route = router.currentRoute.value;
	const { profileSwitchRedirect } = route.meta;

	if (!(await canAccessRoute(route.meta))) {
		await router.push({ name: userStore.profileDefaultRoute });
	} else if (profileSwitchRedirect) {
		const redirect = typeof profileSwitchRedirect === 'function'
			? profileSwitchRedirect()
			: profileSwitchRedirect;
		await router.push({ name: redirect });
	}
}

function getTestId(role: string, index: number) {
	return `navigationProfileSwitcherItem_Role${role}_Item${index + 1}`;
}

function getTooltipTestId(role: string, index: number) {
	return `navigationProfileSwitcherItemTooltip_Role${role}_Item${index + 1}`;
}
</script>

<style scoped lang="scss">
.the-navigation-menu-profile-switcher {
	&__checkmark {
		bottom: -6px;
		right: -6px;
	}
}
</style>
